import "./index.css";
import { ResourceLabelComponent } from "../ResourceLabelComponent/index-v2";
import { useEffect, useRef, useState, useCallback } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import { darken, readableColor, setLightness } from "polished";
import { Accordion } from "radix-ui";
import React from "react";
import { useAccordionScroll } from "../../hooks/useAccordionScroll";
import flechaBlanca from "../../assets/decidido/flecha_blanca.png";

const LABEL = "label";

export const CourseContentModuleRowComponent = ({ module, moodle, idx, container, navigating, contentFixedBar }) => {
  const theme = useTheme();
  const contentRef = useRef(null);
  const rowRef = useRef(null);

  const newModules = module.modules.filter((m) => m.modname !== LABEL);
  const isUserVisible = module.uservisible;

  const isCurrentModule = module.id === moodle.currentModuleId;
  const isCurrentResource = module.modules.some((r) => r.id === moodle.currentResourceId);

  const moduleBg = isCurrentModule
    ? theme.colors.backgroundColorCurrentModule
    : theme.mode === "light" ? theme.colors.button_light : theme.colors.button_dark;

  const textColorByContrast = readableColor(moduleBg, "#1a1a1a", "#f2f2f2", false);

  const arrowFilter = readableColor(moduleBg, "brightness(0)", "none", false);

  const shouldAutoOpen = isCurrentModule || isCurrentResource;
  const [isOpen, setIsOpen] = useState(shouldAutoOpen);
  const manuallyClosedRef = useRef(false);
  const prevCurrentModuleIdRef = useRef(moodle.currentModuleId);

  // Resetear estado de apertura cuando cambia el módulo o la vista (vía key en el padre)
  useEffect(() => {
    setIsOpen(shouldAutoOpen);
    manuallyClosedRef.current = false;
  }, [module.id, shouldAutoOpen]);

  // Hook para coordinar scroll con Accordion
  const { scheduleScroll, executeScheduledScroll } = useAccordionScroll({
    containerId: 'scrollable-modules-wrapper',
    stickyOffset: contentFixedBar || 0,
    additionalOffset: 0
  });

  // Auto-abrir solo en carga inicial o cuando cambia el módulo activo
  // No reabrir si el usuario cerró manualmente
  useEffect(() => {
    const moduleChanged = prevCurrentModuleIdRef.current !== moodle.currentModuleId;
    prevCurrentModuleIdRef.current = moodle.currentModuleId;

    // Si cambió el módulo activo, resetear el flag de cierre manual
    if (moduleChanged) {
      manuallyClosedRef.current = false;
    }

    // Solo abrir si:
    // 1. Debe estar abierto (es el módulo activo o contiene el recurso activo)
    // 2. No está abierto actualmente
    // 3. El usuario no lo cerró manualmente
    if (shouldAutoOpen && !isOpen && !manuallyClosedRef.current) {
      setIsOpen(true);
      // Programar scroll al módulo cuando se abre automáticamente
      if (isCurrentModule) {
        scheduleScroll(rowRef.current);
      }
    }
  }, [shouldAutoOpen, isOpen, isCurrentModule, scheduleScroll, moodle.currentModuleId]);

  /**
   * Handler cuando el usuario hace clic en el trigger del Accordion
   * - Al abrir cualquier módulo: scroll a su header
   * - Al cerrar: marcar como cerrado manualmente si es el módulo activo
   */
  const handleValueChange = useCallback((value) => {
    const willOpen = value === "item-1";
    setIsOpen(willOpen);

    // Si el usuario cierra el módulo activo, marcar como cerrado manualmente
    if (!willOpen && isCurrentModule) {
      manuallyClosedRef.current = true;
    }

    // Programar scroll al header cuando se abre cualquier módulo
    if (willOpen) {
      scheduleScroll(rowRef.current);
    }
  }, [scheduleScroll, isCurrentModule]);

  /**
   * Handler cuando termina la animación del Accordion
   * - Al abrir cualquier módulo: scroll a su header
   * - Al cerrar módulo activo: scroll al módulo activo
   * - Al cerrar módulo no activo: scroll al módulo activo
   */
  const handleAccordionAnimationEnd = useCallback(() => {
    if (isOpen) {
      // Cualquier módulo se abrió → ejecutar scroll programado a su header
      executeScheduledScroll();
    } else {
      // Módulo se cerró
      if (isCurrentModule) {
        // Módulo activo se cerró → scroll al módulo activo actual
        const activeModule = document.getElementById("current-active-module");
        if (activeModule) {
          scheduleScroll(activeModule);
          executeScheduledScroll();
        }
      } else {
        // Módulo no activo se cerró → scroll al módulo activo
        const activeModule = document.getElementById("current-active-module");
        if (activeModule) {
          scheduleScroll(activeModule);
          executeScheduledScroll();
        }
      }
    }
  }, [isOpen, isCurrentModule, scheduleScroll, executeScheduledScroll]);

  return (
    <Accordion.Root
      ref={rowRef}
      className={`AccordionRoot wrapper-row ${isCurrentResource ? "current-resource-container" : ""}`}
      type="single"
      value={isOpen ? "item-1" : ""}
      onValueChange={handleValueChange}
      collapsible
      data-module-id={module.id}
      data-contains-resource-id={isCurrentResource ? moodle.currentResourceId : ""}
      style={{
        opacity: 1,
        pointerEvents: !isUserVisible ? "none" : "unset",
        display: !isUserVisible ? "none" : "block",
        boxShadow: isOpen ? "0px 10px 30px rgba(0,0,0,0.15)" : "none",
        /* margin: isOpen ? "16px 0" : "4px 0", */
        transition: "all 0.3s ease",
        borderRadius: "12px",
      }}
    >
      <Accordion.Item className={`AccordionItem ${isCurrentModule ? "module-border-active" : "module-border"}`} value="item-1">
        {/* HEADER → Trigger */}
        <AccordionTrigger asChild>
            <div
              className={`row-container rounded-t-xl module-header ${isOpen ? "is-open" : ""}`}
              id={isCurrentModule ? "current-active-module" : ""}
              style={{
                paddingTop: "15px",
                paddingBottom: "15px",
                borderRadius: isOpen ? "6px 0px 0px 0px" : "6px",
                backgroundColor: isCurrentModule
                  ? "var(--detail-module-bg-open)"
                  : "var(--detail-module-bg)",
                borderLeft: isCurrentModule
                   ? theme.mode === "light" ? "1px solid var(--detail-module-bg-open)" : "1px solid rgba(255, 255, 255, 0.55)"
                  : "1px solid transparent",
                borderRight: isCurrentModule
                  ? theme.mode === "light" ? "1px solid var(--detail-module-bg-open)" : "1px solid rgba(255, 255, 255, 0.55)"
                  : "1px solid transparent",
                borderTop: isCurrentModule
                  ? theme.mode === "light" ? "1px solid var(--detail-module-bg-open)" : "1px solid rgba(255, 255, 255, 0.55)"
                  : "1px solid transparent",
                borderBottom: theme.mode === "light" ? "none" : "1px solid rgba(255, 255, 255, 0.55)"
              }}
            >
            {/* ÍNDICE */}
            <div className="pl-3 flex justify-center items-center">
              <div
                className=" rounded-full w-10 h-10 flex justify-center items-center border-opacity-15 border-[.5px] border-gray-200"
                style={{
                  backgroundColor: isCurrentModule
                    ? "#FBD459"
                    : "var(--detail-module-index-bg)",
                  opacity: isCurrentModule ? 1 : 0.85,
                }}
              >
                <p
                  className="text-2xl font-jost font-semibold"
                  style={{
                    color: isCurrentModule ? "var(--text-on-brand-vibrant)" : "var(--text-on-brand-dark)",
                  }}
                >
                  {idx + 1}
                </p>
              </div>
            </div>

            {/* TÍTULO */}
            <div className="title-container grid" style={{ width: "100%", gap: isOpen ? "4px" : "0px" }}>
              <div
                className="title-btn font-jost text-2xl"
                style={{
                  color: isCurrentModule ? "var(--text-on-brand-dark)" : "var(--text-on-brand-vibrant)",
                  textDecoration: "underline",
                  fontSize:  "16px",
                  textDecorationColor: isCurrentModule ? "var(--text-on-brand-dark)" : "transparent",
                  fontWeight: "600",
                }}
              >
                {module.name}
              </div>
              {module.hasProgress && (
                <ProgressBar
                  progress={module.rawProgress}
                  isCurrentModule={isCurrentModule}
                />
              )}
              {isOpen && module.availabilityinfo && (
                <p
                  style={{ fontSize: 10, marginTop: 2, opacity: 1, color: isCurrentModule ? "var(--text-on-brand-dark)" : "var(--text-on-brand-vibrant)" }}
                  dangerouslySetInnerHTML={{
                    __html: module.availabilityinfo,
                  }}
                />
              )}
              {isOpen && module.summary && (
                <div
                  style={{ fontSize: 10, marginTop: 2, opacity: 1, color: isCurrentModule ? "var(--text-on-brand-dark)" : "var(--text-on-brand-vibrant)" }}
                  dangerouslySetInnerHTML={{
                    __html: module.summary,
                  }}
                />
              )}
            </div>

            {/* ICONO */}
            {newModules?.length > 0 && (
              <div className={`AccordionChevronWrapper`}>
                <img
                  src={flechaBlanca}
                  alt=""
                  aria-hidden="true"
                  style={{
                    width: 24,
                    height: 24,
                    objectFit: "contain",
                    transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                    transition: "transform 0.3s ease",
                    filter: arrowFilter,
                  }}
                />
              </div>
            )}
          </div>
        </AccordionTrigger>

        {/* CONTENT */}
        <Accordion.Content
          onAnimationEnd={handleAccordionAnimationEnd}
          ref={contentRef}
          className="module-content rounded-b-xl AccordionContent module-content-body"
          style={{
            backgroundColor: "var(--module-frozen-bg)",
            borderLeftWidth: isOpen ? "1px" : "0px",
            borderRightWidth: isOpen ? "1px" : "0px",
            borderBottomWidth: isOpen ? "1px" : "0px",
            borderColor: theme.mode === "light" ? "#122D4B44" : "rgba(255, 255, 255, 0.55)",
          }}
        >
          <div className="AccordionContentInner">
            {newModules &&
              newModules.map((resource, idx) => (
                <ResourceLabelComponent
                  container={container}
                  module={module}
                  resource={resource}
                  key={resource.id}
                  moodle={moodle}
                  idx={idx}
                  isCurrentModule={isCurrentModule}
                  isModuleOpen={isOpen}
                  contentFixedBar={contentFixedBar}
                />
              ))}
          </div>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );
};

const ProgressBar = ({ progress, isCurrentModule }) => {
  const progressWidth = Math.min(Math.max(progress || 0, 0), 100);

  return (
    <div className="w-full mt-1">
      <div
        style={{
          backgroundColor: isCurrentModule ? "rgba(255, 255, 255, 0.25)": "rgba(255, 255, 255, 0.85)",
          width: "100%",
          height: 5,
          borderRadius: "5px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            backgroundColor: isCurrentModule
              ? "var(--text-on-brand-dark)"
              : "var(--text-on-brand-vibrant)",
            width: `${progressWidth}%`,
            height: 5,
            borderRadius: "5px",
            transition: "width 0.3s ease",
          }}
        />
      </div>
      <span
        style={{
          color: isCurrentModule
            ? "var(--text-on-brand-dark)"
            : "var(--text-on-brand-vibrant)",
          fontSize: "10px",
          marginTop: "4px",
          display: "inline-block",
        }}
      >
        {progressWidth}% completado
      </span>
    </div>
  );
};

const AccordionTrigger = React.forwardRef(({ children, ...props }, ref) => (
  <Accordion.Header>
    <Accordion.Trigger {...props} ref={ref}>
      {children}
    </Accordion.Trigger>
  </Accordion.Header>
));
